<template>
  <div class="container">
    <el-card class="box-card" :body-style="{ padding: '0' }">
      <!-- 表格头部操作栏 -->
      <div style="display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 0; width: 100%;">
        <!-- 选择省份 -->
        <el-select
          v-model="params.selectedProvince"
          placeholder="选择省份"
          clearable
          style="width: 170px; flex-shrink: 0;"
          @change="handleFilterChange">
          <el-option label="全国" value="" />
          <el-option
            v-for="p in provinces"
            :key="p"
            :label="p"
            :value="p" />
        </el-select>

        <!-- 设备状态 -->
        <el-select
          v-model="params.selectedStatus"
          placeholder="请选择设备状态"
          clearable
          style="width: 150px; flex-shrink: 0;"
          @change="handleFilterChange">
          <el-option label="在线" value="online" />
          <el-option label="正常" value="0" />
          <el-option label="报警" value="1" />
          <el-option label="离线" value="2" />
        </el-select>

        <!-- 联机状态 -->
        <el-select
          v-model="params.selectedOnlineStatus"
          placeholder="请选择联机状态"
          clearable
          style="width: 150px; flex-shrink: 0;"
          @change="handleFilterChange">
          <el-option label="单机" value="0" />
          <el-option label="联机" value="1" />
        </el-select>

        <!-- 配对状态 -->
        <el-select
          v-model="params.selectedPairStatus"
          placeholder="请选择配对状态"
          clearable
          style="width: 150px; flex-shrink: 0;"
          @change="handleFilterChange">
          <el-option label="未配对" value="0" />
          <el-option label="配对中" value="1" />
          <el-option label="已配对" value="2" />
        </el-select>

        <!-- 搜索设备ID/批次号 -->
        <el-input
          placeholder="搜索设备ID/批次号"
          v-model="params.searchKeyword"
          clearable
          @clear="handleSearchClear"
          @keyup.enter="handleSearch"
          style="width: 260px; flex-shrink: 0;">
          <el-button slot="append" icon="el-icon-search" @click="handleSearch" />
        </el-input>

        <!-- 重置按钮 -->
        <el-button type="primary" @click="resetSearch" style="flex-shrink: 0; margin: 0;">重置</el-button>

        <!-- 占位空间：自动占满剩余空间 -->
        <div style="flex: 1;"></div>

        <!-- 刷新按钮：永远在最右边 -->
        <div style="align-self: flex-start; flex-shrink: 0;">
          <el-button
            type="text"
            icon="el-icon-refresh"
            :loading="refreshLoading"
            @click="refreshData"
            style="color: gray; padding: 0;">
            刷新
          </el-button>
        </div>
      </div>

      <!-- 表格 - 固定高度滚动 -->
      <div class="table-container">
        <el-table
          :data="paginatedDevices"
          v-loading="tableLoading"
          border
          stripe
          height="100%"
          :row-style="{height: '6vh'}"
          :cell-style="{padding: '0'}"
        >
          <el-table-column
            label="序号"
            type="index"
            :index="indexMethod"
            width="60"
            align="center"
            fixed="left"
          />
          <el-table-column prop="batchNo" label="批次号" min-width="180" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.batchNo || '未知' }}
            </template>
          </el-table-column>
          <el-table-column prop="deviceId" label="设备ID" min-width="180" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.deviceId || '未知' }}
            </template>
          </el-table-column>
          <el-table-column label="省份" min-width="150" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.province || '未知' }}
            </template>
          </el-table-column>
          <el-table-column label="位置" min-width="180" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ formatCoordinate(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column label="报警电压等级" min-width="100" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.voltageLevel || '未知' }}
            </template>
          </el-table-column>
          <el-table-column label="设备状态" min-width="90" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.deviceStatus)" size="small">
                {{ getStatusText(scope.row.deviceStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="联机状态" min-width="80" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <el-tag :type="getOnlineStatusTagType(scope.row.onlineStatus)" size="small">
                {{ getOnlineStatusText(scope.row.onlineStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="配对状态" min-width="90" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <el-tag :type="getPairStatusTagType(scope.row)" size="small">
                {{ getPairStatusText(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="最后更新" min-width="160" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ formatTime(scope.row.timestamp) }}
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页区域 -->
      <div>
        <el-pagination
          @size-change="handlePageSizeChange"
          @current-change="handleCurrentPageChange"
          :current-page="params.currentPage"
          :page-sizes="[50, 100, 500, 1000]"
          :page-size="params.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredDevices.length"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import WebSocketService from '@/components/websocket/webSocketService'
import axios from 'axios'
import { getAllProvinces } from '@/components/gps/provinceUtils'

export default {
  name: 'DeviceTable',
  data() {
    return {
      provinces: getAllProvinces(),
      deviceMap: new Map(),
      deviceList: [],
      filteredDevices: [],
      tableLoading: false,
      refreshLoading: false,
      wsConnected: false,
      filterDebounceTimer: null,
      // 统一参数管理
      params: {
        searchKeyword: '',
        selectedProvince: '',
        selectedStatus: '',
        selectedOnlineStatus: '',
        selectedPairStatus: '',
        currentPage: 1,
        pageSize: 50
      }
    }
  },
  computed: {
    ...mapGetters('globalUserInfo', ['userId','token']),
    paginatedDevices() {
      const start = (this.params.currentPage - 1) * this.params.pageSize
      const end = start + this.params.pageSize
      return this.filteredDevices.slice(start, end)
    },
    cacheKeyParams() {
      return 'deviceDataCache'
    },
    // 动态生成缓存 key（包含用户ID）
    cacheKeyDeviceMap() {
      return this.userId ? `${this.userId}_deviceDataCache_map` : 'deviceDataCache_map_temp'
    }
  },
  watch: {
    'params.currentPage'() {
      this.saveParamsCache()
    },
    'params.pageSize'() {
      this.params.currentPage = 1
      this.saveParamsCache()
    },
    deviceMap: {
      handler() {
        this.refreshDeviceList()
      },
      deep: true
    }
  },
  async created() {
    await this.restoreCache()
    this.initWebSocket()
    this.loadHistoryData()
  },
  beforeDestroy() {
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer)
    }
    if (this.wsService && this.wsService.onMessageCallbacks) {
      var self = this
      this.wsService.onMessageCallbacks = this.wsService.onMessageCallbacks.filter(
        function(cb) {
          return cb.toString() !== self.handleWebSocketMessage.toString()
        }
      )
    }
  },
  methods: {
    ...mapActions('tabs', ['savePageCache']),

    getCurrentUserId() {
      try {
        const globalUserInfo = this.$store.state.globalUserInfo
        if (globalUserInfo && globalUserInfo.user && globalUserInfo.user.userId) {
          return globalUserInfo.user.userId
        }
      } catch (e) {
        console.error('从Vuex获取userId失败:', e)
      }
      const sessionUserId = sessionStorage.getItem('userId')
      if (sessionUserId) {
        return sessionUserId
      }
      return null
    },

    /**
     * 检查坐标值是否有效
     * 排除：undefined、null、空字符串、0、"0"、NaN
     */
    isValidCoordinate(value) {
      // 检查 undefined、null、空字符串
      if (value === undefined || value === null || value === '') {
        return false
      }

      // 转换为数字检查
      const num = Number(value)

      // 检查是否为 NaN 或 0
      if (isNaN(num) || num === 0) {
        return false
      }

      return true
    },

    /**
     * 获取并格式化坐标字符串
     * 优先使用 gps 对象，其次使用直接的 lat/lng 字段
     * 无效时返回 '未知'
     */
    formatCoordinate(row) {
      if (!row) return '未知'

      let lat, lng

      // 优先使用 gps 对象
      if (row.gps && typeof row.gps === 'object') {
        lat = row.gps.lat
        lng = row.gps.lng
        if (this.isValidCoordinate(lat) && this.isValidCoordinate(lng)) {
          return `${Number(lng).toFixed(4)}, ${Number(lat).toFixed(4)}`
        }
      }

      // 使用直接的 lat/lng 字段
      if (row.lat !== undefined && row.lng !== undefined) {
        lat = row.lat
        lng = row.lng
        if (this.isValidCoordinate(lat) && this.isValidCoordinate(lng)) {
          return `${Number(lng).toFixed(4)}, ${Number(lat).toFixed(4)}`
        }
      }

      return '未知'
    },

    handleFilterChange() {
      this.params.currentPage = 1
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
      }
      this.filterDebounceTimer = setTimeout(() => {
        this.filterDevices()
      }, 300)
      this.saveParamsCache()
    },

    async restoreCache() {
      try {
        // 恢复页面参数缓存
        const cachedParams = sessionStorage.getItem(this.cacheKeyParams)
        if (cachedParams) {
          try {
            const params = JSON.parse(cachedParams)
            this.params = params
            console.log('恢复页面参数缓存成功')
          } catch (e) {
            console.error('解析页面参数缓存失败:', e)
          }
        }
      } catch (error) {
        console.error('恢复缓存失败:', error)
      }
    },

    saveParamsCache() {
      try {
        sessionStorage.setItem(this.cacheKeyParams, JSON.stringify(this.params))
      } catch (error) {
        console.error('保存页面参数缓存失败:', error)
      }
    },

    indexMethod(index) {
      return (this.params.currentPage - 1) * this.params.pageSize + index + 1
    },

    formatTime(timestamp) {
      if (!timestamp) return '未知'
      const date = new Date(timestamp)
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`
    },

    getStatusText(deviceStatus) {
      if (!deviceStatus) return '离线'
      switch (deviceStatus) {
        case '0': return '正常'
        case '1': return '报警'
        case '2': return '离线'
        default: return '离线'
      }
    },

    getStatusTagType(deviceStatus) {
      if (!deviceStatus) return 'info'
      switch (deviceStatus) {
        case '0': return 'success'
        case '1': return 'danger'
        case '2': return 'info'
        default: return 'info'
      }
    },

    getOnlineStatusText(onlineStatus) {
      if (!onlineStatus) return '单机'
      switch (onlineStatus) {
        case '0': return '单机'
        case '1': return '联机'
        default: return '单机'
      }
    },

    getOnlineStatusTagType(onlineStatus) {
      if (!onlineStatus) return 'info'
      switch (onlineStatus) {
        case '0': return 'info'
        case '1': return 'success'
        default: return 'info'
      }
    },

    /**
     * 获取配对状态文本
     * 规则：
     * - online_status=0, pair_status=1 → "配对中"
     * - online_status=0, pair_status=0 → "未配对"
     * - online_status=1, pair_status=0 → "已配对"
     */
    getPairStatusText(row) {
      const onlineStatus = row.onlineStatus
      const pairStatus = row.pairStatus

      // online_status=0, pair_status=1 → 配对中
      if (onlineStatus === '0' && pairStatus === '1') {
        return '配对中'
      }
      // online_status=0, pair_status=0 → 未配对
      if (onlineStatus === '0' && pairStatus === '0') {
        return '未配对'
      }
      // online_status=1, pair_status=0 → 已配对
      if (onlineStatus === '1' && pairStatus === '0') {
        return '已配对'
      }
      return '未知'
    },

    /**
     * 获取配对状态标签样式
     */
    getPairStatusTagType(row) {
      const onlineStatus = row.onlineStatus
      const pairStatus = row.pairStatus

      // online_status=0, pair_status=1 → 配对中 (warning)
      if (onlineStatus === '0' && pairStatus === '1') {
        return 'warning'
      }
      // online_status=0, pair_status=0 → 未配对 (info)
      if (onlineStatus === '0' && pairStatus === '0') {
        return 'info'
      }
      // online_status=1, pair_status=0 → 已配对 (success)
      if (onlineStatus === '1' && pairStatus === '0') {
        return 'success'
      }
      return 'info'
    },

    async loadHistoryData() {
      const userId = this.userId || this.getCurrentUserId()
      if (!userId) {
        console.warn('用户未登录，无法获取设备数据')
        this.$message.warning('请先登录')
        return
      }
      this.tableLoading = true
      try {
        const response = await axios.get('/device/list', {
          params: {
            userId: userId
          }
        })
        if (response.data && response.data.success === true) {
          const data = response.data.data || {}
          const devices = data.list || []
          this.deviceMap.clear()
          devices.forEach(device => {
            if (device.deviceId) {
              this.deviceMap.set(device.deviceId, device)
            }
          })
          this.refreshDeviceList()
        } else {
          var errorMsg = '未知错误'
          if (response.data && response.data.message) {
            errorMsg = response.data.message
          }
          console.error('加载设备数据失败:', errorMsg)
          this.filteredDevices = []
        }
      } catch (error) {
        console.error('加载历史数据失败:', error)
        if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
          this.$message.error('请求超时，数据量过大，请稍后重试')
        } else {
          this.$message.error('网络错误，请稍后重试')
        }
        this.filteredDevices = []
      } finally {
        this.tableLoading = false
      }
    },

    refreshDeviceList() {
      this.deviceList = Array.from(this.deviceMap.values())
      this.filterDevices()
    },

    filterDevices() {
      let result = [...this.deviceList]

      if (this.params.searchKeyword) {
        const keyword = this.params.searchKeyword.toLowerCase()
        result = result.filter(device =>
          device.deviceId.toLowerCase().includes(keyword) ||
          (device.batchNo && device.batchNo.toLowerCase().includes(keyword))
        )
      }

      if (this.params.selectedProvince) {
        result = result.filter(device => device.province === this.params.selectedProvince)
      }

      if (this.params.selectedStatus !== '') {
        if (this.params.selectedStatus === 'online') {
          // 在线 = 正常 + 报警
          result = result.filter(device => device.deviceStatus === '0' || device.deviceStatus === '1')
        } else {
          result = result.filter(device => device.deviceStatus === this.params.selectedStatus)
        }
      }

      if (this.params.selectedOnlineStatus !== '') {
        result = result.filter(device => device.onlineStatus === this.params.selectedOnlineStatus)
      }

      if (this.params.selectedPairStatus !== '') {
        result = result.filter(device => {
          const onlineStatus = device.onlineStatus
          const pairStatus = device.pairStatus

          if (this.params.selectedPairStatus === '0') {
            return onlineStatus === '0' && pairStatus === '0'
          }
          if (this.params.selectedPairStatus === '1') {
            return onlineStatus === '0' && pairStatus === '1'
          }
          if (this.params.selectedPairStatus === '2') {
            return onlineStatus === '1' && pairStatus === '0'
          }
          return true
        })
      }

      // 多级排序：设备状态 → 联机状态 → 配对状态
      result.sort((a, b) => {
        // 第一级：设备状态（报警 > 正常 > 离线）
        const getStatusPriority = (status) => {
          if (status === '1') return 1
          if (status === '0') return 2
          if (status === '2') return 3
          return 4
        }
        const statusDiff = getStatusPriority(a.deviceStatus) - getStatusPriority(b.deviceStatus)
        if (statusDiff !== 0) return statusDiff

        // 第二级：联机状态（联机 > 单机）
        const getOnlinePriority = (onlineStatus) => {
          if (onlineStatus === '1') return 1
          if (onlineStatus === '0') return 2
          return 3
        }
        const onlineDiff = getOnlinePriority(a.onlineStatus) - getOnlinePriority(b.onlineStatus)
        if (onlineDiff !== 0) return onlineDiff

        // 第三级：配对状态（已配对 > 配对中 > 未配对）
        const getPairPriority = (row) => {
          const onlineStatus = row.onlineStatus
          const pairStatus = row.pairStatus
          if (onlineStatus === '1' && pairStatus === '0') return 1      // 已配对
          if (onlineStatus === '0' && pairStatus === '1') return 2      // 配对中
          if (onlineStatus === '0' && pairStatus === '0') return 3      // 未配对
          return 4
        }
        return getPairPriority(a) - getPairPriority(b)
      })

      this.filteredDevices = result
    },

    refreshData() {
      this.refreshLoading = true
      this.loadHistoryData().finally(() => {
        this.refreshLoading = false
      })
    },

    handlePageSizeChange(size) {
      this.params.pageSize = size
      this.params.currentPage = 1
    },

    handleCurrentPageChange(page) {
      this.params.currentPage = page
    },

    handleSearch() {
      this.params.currentPage = 1
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
      }
      this.filterDevices()
      this.saveParamsCache()
    },

    handleSearchClear() {
      this.params.searchKeyword = ''
      this.params.currentPage = 1
      this.filterDevices()
      this.saveParamsCache()
    },

    resetSearch() {
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
      }
      this.params.searchKeyword = ''
      this.params.selectedProvince = ''
      this.params.selectedStatus = ''
      this.params.selectedOnlineStatus = ''
      this.params.selectedPairStatus = ''
      this.params.currentPage = 1
      this.filterDevices()
      this.saveParamsCache()
    },

    handleWebSocketMessage(data) {
      try {
        // 用户绑定批次号变更通知
        if (data.type === 'user_bind_change') {
          this.clearAllDeviceData()
          return
        }

        // 设备缓存清除通知
        if (data.type === 'cache_cleared') {
          this.clearAllDeviceData()
          return
        }

        if (data.type === 'device_data' && data.dataType === 'all' && Array.isArray(data.data)) {
          for (let i = 0; i < data.data.length; i++) {
            this.processSingleDevice(data.data[i])
          }
          this.refreshDeviceList()
          return
        }

        if (data.deviceId) {
          this.processSingleDevice(data)
          this.refreshDeviceList()
          return
        }

        if (data.data && data.data.deviceId) {
          this.processSingleDevice(data.data)
          this.refreshDeviceList()
          return
        }
      } catch (e) {
        console.error('解析消息失败:', e)
      }
    },

    // ==================== 数据清理方法 ====================
    /**
     * 清空所有设备数据（用户绑定批次号变更通知、设备缓存清除通知）
     */
    clearAllDeviceData(keepMapState = false) {
      // 清空所有数据
      this.deviceMap.clear()
      this.deviceList = []
      this.filteredDevices = []
      return
    },

    processSingleDevice(data) {
      if (!data.deviceId) return

      // 处理 GPS 数据，过滤无效值
      let gps = null
      let lat = data.lat
      let lng = data.lng

      // 检查 lat/lng 是否有效
      const isValidLat = this.isValidCoordinate(lat)
      const isValidLng = this.isValidCoordinate(lng)

      if (isValidLat && isValidLng) {
        gps = {
          lat: parseFloat(lat),
          lng: parseFloat(lng)
        }
      } else {
        // 如果无效，设置为 undefined
        lat = undefined
        lng = undefined
      }

      const formattedDevice = {
        deviceId: data.deviceId,
        batchNo: data.batchNo,
        currentVoltage: data.current_voltage || data.currentVoltage,
        voltageLevel: data.voltage_level || data.voltageLevel,
        deviceStatus: data.device_status || data.deviceStatus,
        onlineStatus: data.online_status || data.onlineStatus,
        pairStatus: data.pair_status || data.pairStatus,
        province: data.province || '',
        gps: gps,
        lat: lat,
        lng: lng,
        timestamp: data.timestamp || Date.now(),
        originalStatus: data.originalStatus
      }

      this.deviceMap.set(formattedDevice.deviceId, formattedDevice)
    },

    initWebSocket() {
      var self = this
      var token = this.token
      if (!token) {
        console.log('Token 为空，不建立 WebSocket 连接')
        return
      }
      // 使用 WebSocketService 单例
      this.wsService = new WebSocketService()
      this.wsConnected = this.wsService.isConnected
      var handler = function(data) {
        self.handleWebSocketMessage(data)
      }
      this.wsService.onMessage(handler)
      this.wsService.onOpen(function() {
        self.wsConnected = true
        console.log('DeviceTable: WebSocket 已连接')
      })
      this.wsService.onClose(function() {
        self.wsConnected = false
        console.log('DeviceTable: WebSocket 已断开')
      })
    }
  }
}
</script>
